import { useMutation } from 'react-query';
import { Lists } from '~/api/scheme';
import { useForm } from 'react-hook-form';
import useWarnings from '@hooks/useWarnings';
import { ListFormValues } from '../types';
import { DEFAULT_CREATE_PARAMS } from '../constants';
import { CreateParams } from '~/api/scheme/lists/create';
import useListDialogs from '~/hooks/useListDialogs';
import useCreditValidator from '~/hooks/useCreditValidator';
import useCurrentUser from '~/hooks/useCurrentUser';
import { useRouter } from 'next/router';

const useCreateListDialog = () => {
  const { changeWarningsState } = useWarnings();

  const {
    createListOpened: open,
    openLeavePage: onClose,
    closeCreateListDialog,
    onClickBack,
    leavePageOpened: isOpenLeavePage,
    selectedIds,
    selectedRows,
    listsRefetch,
    onCreateListSuccessCallback,
    creatableListType,
    onCancel,
    onClickLeaveAnyway,
  } = useListDialogs();
  const { validateCredits } = useCreditValidator();
  const { currentUser } = useCurrentUser();
  const router = useRouter();

  const mutation = useMutation((params: CreateParams) => Lists.create(params), {
    onSuccess({
      data: {
        data: {
          data: { id: listId },
          meta: { sync_success },
        },
      },
    }) {
      if (!sync_success && sync_success !== null) {
        changeWarningsState('syncList', true);
      }
      listsRefetch();
      onCreateListSuccessCallback?.(listId, selectedIds);
    },
    onError() {
      changeWarningsState('syncList', true);
    },
  });

  const methods = useForm<ListFormValues>({ defaultValues: DEFAULT_CREATE_PARAMS });
  const { reset } = methods;

  const toApiAttributes = (values: ListFormValues): CreateParams['attributes'] => {
    const isShared = values.visibility === 'shared';
    const isSharedAll = isShared && values.shared_scope === 'all';
    const sharedIds =
      isShared && values.shared_scope === 'specific'
        ? values.shared_user_ids.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
        : [];

    return {
      color: values.color,
      description: values.description,
      name: values.name,
      synced: values.synced,
      public: isSharedAll,
      shared_user_ids: sharedIds,
    };
  };

  const onSubmit = () => {
    const formValues = methods.getValues();
    const listAttributes = toApiAttributes(formValues);
    if (
      creatableListType === 'OpportunityList' &&
      selectedIds.length > 0 &&
      !router.pathname.includes('/opportunities/lists')
    ) {
      validateCredits('list', '', selectedRows, currentUser?.attributes.credits_amount || 0, 1, {
        attributes: listAttributes,
        type: creatableListType,
      });
    } else {
      mutation.mutate({ attributes: listAttributes, type: creatableListType });
      reset(DEFAULT_CREATE_PARAMS);
      closeCreateListDialog();
    }
  };

  const onCreateList = (listAttributes: CreateParams) => {
    mutation.mutate(listAttributes);
    reset(DEFAULT_CREATE_PARAMS);
  };

  const onReset = () => {
    reset(DEFAULT_CREATE_PARAMS);
  };

  return {
    open,
    onClose,
    onClickBack,
    isOpenLeavePage,
    onCancel,
    onClickLeaveAnyway,
    onSubmit,
    onCreateList,
    onReset,
    methods,
    creatableListType,
    selectedIds,
    dialogTitle: 'create_new_list',
    submitButtonTitle: 'create',
  };
};

export default useCreateListDialog;
